// src/components/Header.jsx
import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { logout as logoutAction } from "../redux/slices/authSlice";
import SOURCE_MAP from "../config/sourceMap";
import { getRemainingMs as getSessionRemainingMs, getSessionDurationMs, restoreSessionTiming } from "../utils/sessionTimer";
import { SCREEN, usePermissions } from "../utils/permissions";
import useWeightDisputeAttention from "../hooks/useWeightDisputeAttention";
import {
  Bell,
  Lock,
  LogOut,
  ChevronDown,
  Wallet as WalletIcon,
  Plus,
  AlertTriangle,
  Info,
  CheckCircle2,
  Megaphone,
  X as Close,
  Search,
  Loader2,
  TrendingUp,
  Package,
  FileText,
  Settings,
  HelpCircle,
  Clock,
  Activity,
} from "lucide-react";
import { fetchWalletBalance } from "../redux/slices/walletSlice";

// ===============================
// --- The Glide ---
// One soft highlight per list that slides between rows instead of
// per-row hover backgrounds ("box bann k popup" fix). First appearance
// positions instantly and fades in - it never slides from a stale spot.
// ===============================
function useGlide() {
  const containerRef = useRef(null);
  const visibleRef = useRef(false);
  const pendingRef = useRef(null);
  const frameRef = useRef(0);
  const [glide, setGlide] = useState({ top: 0, left: 0, width: 0, height: 0, opacity: 0, immediate: true });

  useEffect(() => () => cancelAnimationFrame(frameRef.current), []);

  // Snap to hidden with transitions suppressed - used whenever a panel (re)mounts
  const reset = useCallback(() => {
    visibleRef.current = false;
    pendingRef.current = null;
    setGlide((p) => (p.opacity === 0 && p.immediate ? p : { ...p, opacity: 0, immediate: true }));
  }, []);

  // Callback ref for the list container; panels unmount on close, so a fresh
  // mount always starts with the glide hidden (no Transition enter conflicts).
  const attachContainer = useCallback(
    (node) => {
      containerRef.current = node;
      if (node) reset();
    },
    [reset]
  );

  // Fade to opacity 0 in place (list mouseleave / no target row)
  const hide = useCallback(() => {
    visibleRef.current = false;
    pendingRef.current = null;
    setGlide((p) => (p.opacity === 0 ? p : { ...p, opacity: 0, immediate: false }));
  }, []);

  const move = useCallback((el) => {
    const cont = containerRef.current;
    if (!el || !cont || !cont.contains(el)) return; // guard stale / unmounted row refs
    const cr = cont.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    const next = {
      top: er.top - cr.top + cont.scrollTop,
      left: er.left - cr.left + cont.scrollLeft,
      width: er.width,
      height: er.height,
    };
    if (!visibleRef.current) {
      // Fresh appearance: land in place silently, then fade in on the next frame.
      visibleRef.current = true;
      pendingRef.current = next;
      setGlide({ ...next, opacity: 0, immediate: true });
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = requestAnimationFrame(() => {
          if (!visibleRef.current || !pendingRef.current) return;
          const target = pendingRef.current;
          pendingRef.current = null;
          setGlide({ ...target, opacity: 1, immediate: false });
        });
      });
    } else if (pendingRef.current) {
      // Still waiting for the fade-in frame - keep repositioning instantly.
      pendingRef.current = next;
      setGlide({ ...next, opacity: 0, immediate: true });
    } else {
      setGlide({ ...next, opacity: 1, immediate: false });
    }
  }, []);

  return { glide, attachContainer, move, hide };
}

// The single sliding highlight element a list renders behind its rows
function GlideHighlight({ glide, color }) {
  return (
    <div
      aria-hidden="true"
      className="hdr-glide"
      style={{
        background: color,
        width: glide.width,
        height: glide.height,
        transform: `translate(${glide.left}px, ${glide.top}px)`,
        opacity: glide.opacity,
        transition: glide.immediate
          ? "opacity 150ms ease"
          : "transform 180ms cubic-bezier(.4,0,.2,1), height 180ms cubic-bezier(.4,0,.2,1), width 180ms cubic-bezier(.4,0,.2,1), opacity 150ms ease",
      }}
    />
  );
}

// Menu row that reports its DOM node whenever Headless UI marks it active
// (keyboard focus and pointer hover both flow through `active`), so the
// avatar menu's glide follows either input method with zero per-row bg.
const GlideMenuButton = React.forwardRef(function GlideMenuButton(
  { active, onGlide, className, children, ...rest },
  forwardedRef
) {
  const nodeRef = useRef(null);
  useEffect(() => {
    if (active) onGlide(nodeRef.current);
  }, [active, onGlide]);
  const setRefs = (node) => {
    nodeRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };
  return (
    <button ref={setRefs} type="button" className={className} {...rest}>
      {children}
    </button>
  );
});

/**
 * Rolls a number to its value inside its own memoized leaf, so 60fps frames re-render
 * one span instead of the whole header. First mount rolls up from zero (900ms), later
 * changes roll from the previous value (600ms). Reduced motion snaps.
 */
const RollingNumber = React.memo(function RollingNumber({ value, format }) {
  const [display, setDisplay] = useState(0);
  const displayRef = useRef(0);
  // doneRef marks the last value that FINISHED (or snapped) - assigned only on completion,
  // so StrictMode's double-effect in dev can't swallow the mount animation.
  const doneRef = useRef(null);
  useEffect(() => {
    const to = value;
    if (to == null || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      doneRef.current = to;
      displayRef.current = to ?? 0;
      setDisplay(to);
      return undefined;
    }
    if (doneRef.current === to) {
      setDisplay(to);
      return undefined;
    }
    const from = displayRef.current ?? 0;
    const dur = doneRef.current == null ? 900 : 600;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = from + (to - from) * eased;
      displayRef.current = v;
      setDisplay(v);
      if (p < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        doneRef.current = to;
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{display == null ? "-" : format(display)}</>;
});

export default function Header({ onMenuClick = () => {}, marquees: propMarquees = [] }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const headerRef = useRef(null);
  const scrollProgressRef = useRef(null);

  // Page scroll progress - GPU-cheap scaleX on a fixed 3px bar above the header.
  useEffect(() => {
    const update = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      if (scrollProgressRef.current) {
        scrollProgressRef.current.style.transform = `scaleX(${progress})`;
      }
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);
  const [scrolled, setScrolled] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  // ===============================
  // --- Omnisearch state ---
  // ===============================
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [shipmentHits, setShipmentHits] = useState([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [shipmentsSearched, setShipmentsSearched] = useState(false);
  // Enter pressed while results were still in flight - open the first hit on arrival.
  const pendingEnterRef = useRef(false);
  // Bump to replay the not-found shake - Enter on a miss must visibly answer.
  const [notFoundPulse, setNotFoundPulse] = useState(0);
  const searchInputRef = useRef(null);
  const omniRef = useRef(null);
  const searchSeqRef = useRef(0);

  // Weight-dispute attention (FE stopgap until the backend notification channel).
  const { canScreen: checkWdScreen, isReady: wdPermsReady } = usePermissions();
  const wdAttention = useWeightDisputeAttention(wdPermsReady && checkWdScreen(SCREEN.WEIGHT_DISPUTES));

  // The Glide - one shared sliding highlight per list (omnisearch + avatar menu)
  const omniGlide = useGlide();
  const menuGlide = useGlide();
  const omniRowRefs = useRef(new Map()); // flattened activeIndex -> row DOM node
  const omniNavSourceRef = useRef("key"); // "key" | "mouse" - only keyboard moves auto-scroll the list

  // Safe wallet default to avoid undefined runtime errors
  const wallet = useSelector((s) => s.wallet) || {
    balance: null,
    type: null,
    status: "idle",
    lastUpdated: 0,
    error: null,
  };

  const uiMarquees = useSelector((s) => s.ui?.marquees) || [];


  const merchantId = typeof window !== "undefined" ? localStorage.getItem("merchantId") : null;
  const source =
    useSelector((s) => s.auth?.source) || localStorage.getItem("source") || "wayuvega";
  const brand = SOURCE_MAP[source] || SOURCE_MAP.wayuvega;

  // "Quiet chrome, one accent" - logo azure (#3877FC / deep #2A59BD) for wayuvega, deep teal for jumbomail.
  const isJumbo = source === "jumbomail";
  const focusable = `focus:outline-none focus-visible:ring-2 ${
    isJumbo ? "focus-visible:ring-teal-600/40" : "focus-visible:ring-[#3877FC]/50"
  }`;
  const accentText = isJumbo ? "text-teal-700" : "text-[#2A59BD]";
  const accentBadge = isJumbo ? "bg-teal-50 text-teal-700" : "bg-[#3877FC]/10 text-[#2A59BD]";
  const accentTile = isJumbo ? "bg-teal-50 text-teal-600" : "bg-[#3877FC]/10 text-[#3877FC]";
  const accentAvatarFallback = isJumbo
    ? "linear-gradient(135deg, #0d9488 0%, #134e4a 100%)"
    : "linear-gradient(135deg, #6F9FFD 0%, #3877FC 48%, #1C3B7E 100%)";
  // Soft brand wash for the sliding list highlight (The Glide)
  const glideTint = isJumbo ? "rgba(7,55,74,0.05)" : "rgba(56,119,252,0.08)";

  // Command items - the "Navigate" group of the omnisearch
  const commandItems = useMemo(() => [
    { icon: Package, label: "Create Forward Order", path: "/create-forward-order", shortcut: "C F", category: "Actions" },
    { icon: Package, label: "Create Bulk Order", path: "/create-bulk-order", shortcut: "C B", category: "Actions" },
    { icon: TrendingUp, label: "All Shipments", path: "/all-shipments", shortcut: "A S", category: "Navigation" },
    { icon: FileText, label: "Reports", path: "/reports", shortcut: "R", category: "Navigation" },
    { icon: WalletIcon, label: "Wallet", path: "/wallet", shortcut: "W", category: "Navigation" },
    { icon: Settings, label: "Profile Settings", path: "/profile", shortcut: "P", category: "Settings" },
    { icon: HelpCircle, label: "Help & Support", path: "/support/tickets", shortcut: "H", category: "Help" },
  ], []);

  const filteredCommands = useMemo(() => {
    if (!searchQuery.trim()) return commandItems;
    const query = searchQuery.toLowerCase();
    return commandItems.filter(item =>
      item.label.toLowerCase().includes(query) ||
      item.category.toLowerCase().includes(query)
    );
  }, [searchQuery, commandItems]);

  const token = typeof window !== "undefined" ? localStorage.getItem("authToken") : null;
  let username = "USER";
  if (token) {
    try {
      const decoded = jwtDecode(token);
      const raw = decoded.username || decoded.sub || "USER";
      username = raw && raw.length > 0 ? raw.charAt(0).toUpperCase() + raw.slice(1) : "USER";
    } catch {}
  }

  // ===============================
  // --- Omnisearch logic ---
  // ===============================
  const idQuery = searchQuery.trim();
  // Looks like an AWB / order-reference: >= 6 chars, alphanumeric or dash
  const looksLikeIdentifier = /^[A-Za-z0-9-]{6,}$/.test(idQuery);
  const showShipmentsGroup = looksLikeIdentifier && !!token && !!merchantId;

  const closeSearch = useCallback(() => {
    searchSeqRef.current += 1; // invalidate any in-flight shipment search
    pendingEnterRef.current = false; // a remembered Enter dies with the dropdown
    setSearchOpen(false);
    setSearchQuery("");
    setActiveIndex(0);
    setShipmentHits((prev) => (prev.length ? [] : prev));
    setShipmentsLoading(false);
    setShipmentsSearched(false);
  }, []);

  // Global shortcuts: Cmd/Ctrl+K focuses the omnisearch, Escape blurs + clears + closes
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
        searchInputRef.current?.focus();
      }
      if (e.key === "Escape") {
        searchInputRef.current?.blur();
        closeSearch();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeSearch]);

  // Debounced live shipment search - mirrors the AllShipments search-orders call.
  // Backend ANDs its filters, so query AWB and order-ref in parallel and merge.
  useEffect(() => {
    if (!looksLikeIdentifier || !token || !merchantId) {
      setShipmentHits([]);
      setShipmentsLoading(false);
      setShipmentsSearched(false);
      return undefined;
    }

    const seq = ++searchSeqRef.current;
    setShipmentsLoading(true);
    setShipmentsSearched(false);

    const timer = setTimeout(async () => {
      try {
        const url = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_SEARCH_ORDERS}`;
        const headers = { Authorization: token, "x-merchant-id": merchantId, "Content-Type": "application/json" };
        const baseBody = {
          merchant_id: merchantId,
          limit: 5,
          skip: 0,
          sort_field: "shipment_order_date_time_utc",
          sort_direction: "DESC",
        };

        const [byAwb, byRef] = await Promise.allSettled([
          axios.post(url, { ...baseBody, awb_number: idQuery }, { headers }),
          axios.post(url, { ...baseBody, order_reference_number: idQuery }, { headers }),
        ]);

        if (searchSeqRef.current !== seq) return;

        const seen = new Set();
        const merged = [];
        [byAwb, byRef].forEach((r) => {
          const rows =
            r.status === "fulfilled" && Array.isArray(r.value?.data?.orders) ? r.value.data.orders : [];
          rows.forEach((o) => {
            const key = o?.order_reference_number;
            if (!key || seen.has(key)) return;
            seen.add(key);
            merged.push(o);
          });
        });
        setShipmentHits(merged.slice(0, 5));
      } catch {
        // Swallow errors silently into the empty state - never break the dropdown
        if (searchSeqRef.current === seq) setShipmentHits([]);
      } finally {
        if (searchSeqRef.current === seq) {
          setShipmentsLoading(false);
          setShipmentsSearched(true);
        }
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [idQuery, looksLikeIdentifier, token, merchantId]);

  // Flattened selectable rows across both groups (keyboard highlight spans them)
  const selectableItems = useMemo(() => [
    ...filteredCommands.map((item) => ({ type: "nav", item })),
    ...(showShipmentsGroup ? shipmentHits.map((order) => ({ type: "shipment", item: order })) : []),
  ], [filteredCommands, shipmentHits, showShipmentsGroup]);

  // Keep the highlight anchored to the top whenever the result set changes
  useEffect(() => {
    omniNavSourceRef.current = "key";
    setActiveIndex(0);
  }, [searchQuery, shipmentHits]);

  // Omnisearch x The Glide: the highlight's position derives from activeIndex,
  // so mouse hover and arrow keys drive the exact same gliding element.
  // Re-measures whenever rows stream in/out; missing refs simply hide it.
  const { move: moveOmniGlide, hide: hideOmniGlide } = omniGlide;
  useEffect(() => {
    if (!searchOpen) return;
    if (activeIndex < 0 || activeIndex >= selectableItems.length) {
      hideOmniGlide();
      return;
    }
    const el = omniRowRefs.current.get(activeIndex);
    if (el) {
      moveOmniGlide(el);
      if (omniNavSourceRef.current === "key" && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "nearest" });
      }
    } else {
      hideOmniGlide();
    }
  }, [searchOpen, activeIndex, selectableItems, moveOmniGlide, hideOmniGlide]);

  const handleSelect = useCallback((sel) => {
    if (!sel) return;
    if (sel.type === "nav") {
      navigate(sel.item.path);
    } else if (sel.item?.order_reference_number) {
      navigate(`/shipment-details/${sel.item.order_reference_number}`);
    }
    searchInputRef.current?.blur();
    closeSearch();
  }, [navigate, closeSearch]);

  // A remembered Enter fires as soon as the first hit lands (and only then).
  useEffect(() => {
    if (!pendingEnterRef.current) return;
    if (shipmentsLoading) return;
    pendingEnterRef.current = false;
    if (shipmentHits.length > 0) {
      handleSelect({ type: "shipment", item: shipmentHits[0] });
    } else {
      setNotFoundPulse((n) => n + 1); // the remembered Enter resolved to a miss - say so
    }
  }, [shipmentHits, shipmentsLoading, handleSelect]);

  useEffect(() => {
    pendingEnterRef.current = false; // query edited - stale intent must not fire
  }, [searchQuery]);

  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSearchOpen(true);
      if (selectableItems.length > 0) {
        omniNavSourceRef.current = "key";
        setActiveIndex((i) => (i + 1) % selectableItems.length);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (selectableItems.length > 0) {
        omniNavSourceRef.current = "key";
        setActiveIndex((i) => (i - 1 + selectableItems.length) % selectableItems.length);
      }
    } else if (e.key === "Enter") {
      if (searchOpen && selectableItems[activeIndex]) {
        e.preventDefault();
        handleSelect(selectableItems[activeIndex]);
      } else if (searchOpen && looksLikeIdentifier && (shipmentsLoading || !shipmentsSearched)) {
        // Results still in flight - remember the intent instead of dropping the keypress.
        e.preventDefault();
        pendingEnterRef.current = true;
      } else if (searchOpen && looksLikeIdentifier && shipmentsSearched && shipmentHits.length === 0) {
        // Settled on zero hits - Enter re-shakes the not-found row instead of going dead.
        e.preventDefault();
        setNotFoundPulse((n) => n + 1);
      }
    } else if (e.key === "Escape") {
      e.currentTarget.blur();
      closeSearch();
    }
  };

  // ===============================
  // --- Token expiry timer ---
  // ===============================
  const [remainingMs, setRemainingMs] = useState(null);
  const timerIntervalRef = useRef(null);
  const sessionActiveRef = useRef(false);

  useEffect(() => {
    // On mount or token change, try to restore session timing (handles page reload)
    if (token) {
      restoreSessionTiming();
      const initial = getSessionRemainingMs();
      setRemainingMs(initial);
      sessionActiveRef.current = initial !== null && initial > 0;
    } else {
      setRemainingMs(null);
      sessionActiveRef.current = false;
    }

    const startTimer = () => {
      if (timerIntervalRef.current) return;
      timerIntervalRef.current = setInterval(() => {
        const msLeft = getSessionRemainingMs();
        setRemainingMs(msLeft !== null ? msLeft : null);
      }, 1000);
    };

    const stopTimer = () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };

    if (token) startTimer();
    else stopTimer();

    return () => stopTimer();
  }, [token]);

  // Auto logout when timer reaches zero
  useEffect(() => {
    if (remainingMs == null) return;
    if (remainingMs <= 0) {
      dispatch(logoutAction());
      try {
        localStorage.removeItem("authToken");
        localStorage.removeItem("parentClientId");
        localStorage.removeItem("merchantId");
      } catch {}
      const s = source || localStorage.getItem("source") || "wayuvega";
      const route = (SOURCE_MAP[s] && SOURCE_MAP[s].loginRoute) || "/login";
      navigate(route, { replace: true });
    }
  }, [remainingMs, dispatch, navigate, source]);

  // if token changes in another tab, re-init the expiry
  useEffect(() => {
    const onStorage = (e) => {
      if (!e) return;
      if (e.key === "authToken") {
        if (e.newValue) {
          restoreSessionTiming();
          setRemainingMs(getSessionRemainingMs());
        } else {
          setRemainingMs(null);
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Format time
  const formatTime = (ms) => {
    if (ms == null) return "-";
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    if (hrs > 0) return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const isCritical = remainingMs != null && remainingMs <= 60_000;
  const showTimer = remainingMs != null && token != null;

  // Wallet polling + focus/visibility effects
  useEffect(() => {
    if (token && merchantId) dispatch(fetchWalletBalance());
  }, [dispatch, token, merchantId]);

  useEffect(() => {
    const STALE_MS = 60_000;
    const onFocusOrVisible = () => {
      const last = wallet?.lastUpdated ?? 0;
      if (Date.now() - last > STALE_MS) dispatch(fetchWalletBalance());
    };
    const onFocus = () => onFocusOrVisible();
    const onVisibility = () => {
      if (document.visibilityState === "visible") onFocusOrVisible();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [dispatch, wallet?.lastUpdated]);

  useEffect(() => {
    let id;
    const start = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        id = setInterval(() => dispatch(fetchWalletBalance()), 90_000);
      }
    };
    const stop = () => id && clearInterval(id);
    start();
    const onVisibility = () => {
      stop();
      start();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [dispatch]);

  // Cross-tab sync
  useEffect(() => {
    const bc = new BroadcastChannel("wallet");
    bc.onmessage = (e) => {
      if (e?.data === "refresh-balance") dispatch(fetchWalletBalance());
    };
    return () => bc.close();
  }, [dispatch]);

  // Header chrome
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    const handleClickOutside = (e) => {
      if (showNotifications && !e.target.closest(".notification-container")) {
        setShowNotifications(false);
      }
      if (searchOpen && omniRef.current && !omniRef.current.contains(e.target)) {
        closeSearch();
      }
    };
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications, searchOpen, closeSearch]);

  useEffect(() => {
    const onStorage = (e) => {
      if (!e) return;
      if (e.key === "authToken" && e.oldValue && !e.newValue) {
        dispatch(logoutAction());
        const s = localStorage.getItem("source") || "wayuvega";
        const route = (SOURCE_MAP[s] && SOURCE_MAP[s].loginRoute) || "/login";
        navigate(route, { replace: true });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [dispatch, navigate]);

  const handleLogout = () => {
    dispatch(logoutAction());
    try {
      localStorage.removeItem("authToken");
      localStorage.removeItem("parentClientId");
      localStorage.removeItem("merchantId");
    } catch {}
    const s = source || localStorage.getItem("source") || "wayuvega";
    const route = (SOURCE_MAP[s] && SOURCE_MAP[s].loginRoute) || "/login";
    navigate(route, { replace: true });
  };

  const formatINR = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });
  const balance = wallet?.balance;
  const walletVariant =
    balance == null ? "idle" : balance < 200 ? "low" : balance <= 1000 ? "medium" : "high";

  // Merchant- and context-aware search hints. The realest example is the merchant's
  // own last-viewed shipment (stamped by the details page); wallet state and the
  // current route pick the nudges. Everything here resolves in the search index.
  const location = useLocation();
  const SEARCH_HINTS = useMemo(() => {
    const hints = ["Search pages, AWB or order ref..."];
    const mid = localStorage.getItem("merchantId");
    const lastAwb = mid ? localStorage.getItem(`wv_last_awb_${mid}`) : null;
    const lastRef = mid ? localStorage.getItem(`wv_last_ref_${mid}`) : null;
    hints.push(lastAwb ? `Try your AWB ${lastAwb}` : "Try an AWB - paste it straight in");
    hints.push(lastRef ? `Find your order ${lastRef}` : "Try an order reference - SP-C-4560775");
    hints.push(walletVariant === "low"
      ? "Type 'wallet' to recharge now"
      : "Type 'wallet' or 'reports' to jump");
    const path = location.pathname;
    if (path.startsWith("/all-shipments")) hints.push("Type 'create' to start a forward order");
    else if (path.startsWith("/create-forward-order")) hints.push("Type 'bulk' to upload orders in bulk");
    else if (path.startsWith("/wallet")) hints.push("Type 'reports' for billing exports");
    else hints.push("Type 'all' to see every shipment");
    hints.push("Press ⌘K to search from anywhere");
    hints.push("Type 'help' when you're stuck");
    return hints;
  }, [walletVariant, location.pathname]);

  // Typewriter placeholder: type -> hold -> erase -> next hint. Drives the DOM
  // attribute through the ref so the ~40ms ticks never re-render the header tree.
  // Reduced motion falls back to a plain 4s rotation, no typing.
  useEffect(() => {
    const el = () => searchInputRef.current;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      let i = 0;
      if (el()) el().placeholder = SEARCH_HINTS[0];
      const id = setInterval(() => {
        i = (i + 1) % SEARCH_HINTS.length;
        if (el()) el().placeholder = SEARCH_HINTS[i];
      }, 4000);
      return () => clearInterval(id);
    }
    let idx = 0;
    let pos = 0;
    let phase = "type";
    let timer;
    const tick = () => {
      const hint = SEARCH_HINTS[idx];
      if (phase === "type") {
        pos += 1;
        if (el()) el().placeholder = hint.slice(0, pos) + (pos < hint.length ? "▍" : "");
        if (pos >= hint.length) {
          phase = "hold";
          timer = setTimeout(tick, 2600);
        } else {
          timer = setTimeout(tick, 55 + Math.random() * 50);
        }
      } else if (phase === "hold") {
        phase = "erase";
        timer = setTimeout(tick, 0);
      } else {
        pos -= 1;
        if (el()) el().placeholder = hint.slice(0, pos) + (pos > 0 ? "▍" : "");
        if (pos <= 0) {
          idx = (idx + 1) % SEARCH_HINTS.length;
          phase = "type";
          timer = setTimeout(tick, 380);
        } else {
          timer = setTimeout(tick, 18);
        }
      }
    };
    timer = setTimeout(tick, 400);
    return () => clearTimeout(timer);
  }, [SEARCH_HINTS]);


  // Quiet status dot inside the wallet pill - keeps the balance-level signal at a glance
  const variantDot = {
    idle: "bg-slate-300",
    high: "bg-emerald-500",
    medium: "bg-amber-500",
    low: "bg-rose-500 animate-soft-blink",
  };

  const MARQUEE_VARIANTS = {
    critical: {
      bg: "linear-gradient(135deg, #fee2e2, #fecaca, #fee2e2)",
      border: "rgba(225,29,72,.3)",
      color: "#7f1d1d",
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
    },
    warning: {
      bg: "linear-gradient(135deg, #fff7ed, #ffedd5, #fff7ed)",
      border: "rgba(234,88,12,.3)",
      color: "#7c2d12",
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
    },
    info: {
      bg: "linear-gradient(135deg, #eff6ff, #dbeafe, #eff6ff)",
      border: "rgba(37,99,235,.3)",
      color: "#1e3a8a",
      icon: <Info className="w-3.5 h-3.5" />,
    },
    success: {
      bg: "linear-gradient(135deg, #ecfdf5, #d1fae5, #ecfdf5)",
      border: "rgba(16,185,129,.3)",
      color: "#065f46",
      icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    },
    brand: {
      bg: "linear-gradient(135deg, #eef2ff, #e0e7ff, #eef2ff)",
      border: "rgba(99,102,241,.3)",
      color: "#3730a3",
      icon: <Megaphone className="w-3.5 h-3.5" />,
    },
  };

  const combinedExternal = useMemo(() => {
    const incoming = [
      ...(Array.isArray(propMarquees) ? propMarquees : []),
      ...(Array.isArray(uiMarquees) ? uiMarquees : []),
    ];
    const map = new Map();
    incoming.forEach((m) => {
      if (m && m.id) map.set(m.id, m);
    });
    return Array.from(map.values());
  }, [propMarquees, uiMarquees]);

  const systemMarquees = useMemo(() => {
    const rows = [];
    if (walletVariant === "low") {
      rows.push({
        id: "wallet-low",
        text: "⚡ Low wallet balance - recharge to keep your orders flowing smoothly",
        variant: "critical",
        direction: "rtl",
        cta: { label: "Recharge", to: "/wallet?recharge=1" },
        dismissible: true,
      });
    }
    return rows;
  }, [walletVariant]);

  const allMarquees = [...systemMarquees, ...combinedExternal];

  const [dismissed, setDismissed] = useState(() => {
    try {
      const raw = sessionStorage.getItem("dismissedMarquees");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const handleDismiss = (id) => {
    setDismissed((prev) => {
      const next = { ...prev, [id]: true };
      try {
        sessionStorage.setItem("dismissedMarquees", JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const visibleMarquees = allMarquees.filter((m) => !dismissed[m.id]);

  const BAR_HEIGHT = 18;
  const BAR_GAP = 4;
  const totalBannerHeight =
    visibleMarquees.length > 0 ? visibleMarquees.length * BAR_HEIGHT + (visibleMarquees.length - 1) * BAR_GAP : 0;

  // Shared row shells for the omnisearch dropdown.
  // relative z-[1] keeps row content above the sliding glide; rows own NO hover bg.
  const rowBase =
    "relative z-[1] flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150";
  const groupLabelBase =
    "text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400";

  return (
    <>
      <style>{`
        @keyframes logo-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .logo-shimmer { position: relative; overflow: hidden; }
        .logo-shimmer::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(100deg, transparent 20%, rgba(255,255,255,.55) 50%, transparent 80%);
          background-size: 200% 100%;
          animation: logo-shimmer 3.2s ease-in-out infinite;
          pointer-events: none;
          border-radius: inherit;
        }
        @media (prefers-reduced-motion: reduce) {
          .logo-shimmer::before { animation: none; }
        }

        /* ========================= MOTION ========================= */
        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        @keyframes soft-blink {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: .7; transform: scale(.95); }
        }
        .animate-soft-blink {
          animation: soft-blink 1.5s ease-in-out infinite;
        }

        /* ========================= HOVER LANGUAGE ========================= */
        /* The Glide - the one soft highlight per list that slides between rows.
           Position/size arrive via inline style; it scrolls with list content. */
        .hdr-glide {
          position: absolute;
          top: 0;
          left: 0;
          z-index: 0;
          border-radius: 0.5rem;
          pointer-events: none;
          will-change: transform, height, opacity;
        }

        /* ========================= OMNISEARCH DROPDOWN ========================= */
        .omni-list {
          position: relative; /* anchors the glide in scroll-content coordinates */
          max-height: min(60vh, 400px);
          overflow-y: auto;
          padding: 8px;
        }

        .omni-list::-webkit-scrollbar {
          width: 8px;
        }

        .omni-list::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }

        .omni-list::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }

        .omni-list::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* ========================= MARQUEE ========================= */
        .inside-marquee {
          position: absolute;
          left: 8px;
          right: 8px;
          bottom: 4px;
          pointer-events: auto;
        }

        .inside-marquee-item {
          width: 100%;
          height: 18px;
          border-radius: 999px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,.08), 0 1px 4px rgba(0,0,0,.04);
          display: flex;
          align-items: center;
          padding-left: 10px;
          padding-right: 10px;
          backdrop-filter: blur(10px);
        }

        .inside-marquee-track {
          position: relative;
          white-space: nowrap;
          display: inline-flex;
          gap: 48px;
          will-change: transform;
        }

        .inside-marquee-text {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
          font-size: 11px;
          letter-spacing: .3px;
        }

        .inside-marquee-cta {
          margin-left: auto;
          flex-shrink: 0;
          padding: 3px 10px;
          font-size: 10px;
          font-weight: 700;
          border-radius: 999px;
          cursor: pointer;
          transition: all .2s cubic-bezier(0.16, 1, 0.3, 1);
          backdrop-filter: blur(10px);
        }

        .inside-marquee-cta:hover {
          transform: scale(1.08) translateY(-1px);
          box-shadow: 0 4px 12px rgba(0,0,0,.15);
        }

        .inside-marquee-dismiss {
          flex-shrink: 0;
          margin-left: 8px;
          padding: 2px;
          border-radius: 50%;
          cursor: pointer;
          opacity: .7;
          transition: opacity .2s ease, transform .2s ease;
        }

        .inside-marquee-dismiss:hover {
          opacity: 1;
          transform: rotate(90deg);
        }

        @keyframes marquee-ltr {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        @keyframes marquee-rtl {
          0%   { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }

        /* ========================= BADGE ========================= */
        .notification-badge {
          position: absolute;
          top: -2px;
          right: -2px;
          width: 18px;
          height: 18px;
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: white;
          font-size: 10px;
          font-weight: 700;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 2px 8px rgba(239,68,68,0.4);
          animation: scaleIn .3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        /* ========================= REDUCED MOTION ========================= */
        @media (prefers-reduced-motion: reduce) {
          .inside-marquee-track { animation: none !important; transform: translateX(0) !important; }
          .animate-soft-blink { animation: none; }
          .hdr-glide { transition: none !important; } /* highlight snaps instead of gliding */
          .hdr-lift:hover, .hdr-lift:active { transform: none !important; } /* pills stay grounded */
          .inside-marquee-cta:hover, .inside-marquee-dismiss:hover { transform: none !important; }
          * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
        }
      `}</style>

      {/* Scroll progress - the thin brand line riding the very top edge */}
      <div className="fixed top-0 left-0 right-0 h-[3px] z-[60] pointer-events-none" aria-hidden="true">
        <div
          ref={scrollProgressRef}
          className="h-full w-full origin-left"
          style={{
            transform: "scaleX(0)",
            transition: "transform 80ms linear",
            background: isJumbo
              ? "linear-gradient(90deg, #0d9488, #0f766e, #07374a)"
              : "linear-gradient(90deg, #6F9FFD, #3877FC, #2A59BD)",
          }}
        ></div>
      </div>

      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 h-16 border-b border-slate-200/70 bg-white/90 backdrop-blur-md transition-shadow duration-150 ${
          scrolled ? "shadow-[0_1px_12px_rgba(15,23,42,0.06)]" : ""
        }`}
      >
        <div
          className="h-full px-4 sm:px-6 lg:px-8 flex items-center justify-between relative"
          style={{ paddingBottom: totalBannerHeight > 0 ? totalBannerHeight + 6 : 0 }}
        >
          {/* Left Section - Menu + Brand */}
          <div className="flex items-center gap-2.5 min-w-0">
            <button
              type="button"
              onClick={onMenuClick}
              className={`md:hidden inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-slate-600 transition-[color,transform] duration-150 hover:text-slate-900 active:scale-95 ${focusable}`}
              aria-label="Open menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div className="flex items-center gap-2.5 min-w-0">
              <span className="logo-shimmer inline-flex flex-shrink-0 rounded-lg">
                <img src={brand.logo} alt={`${brand.title} logo`} className="h-9 w-auto rounded-lg" />
              </span>

              <div className="hidden sm:flex flex-col justify-center min-w-0">
                <span
                  className={`text-[15px] font-semibold leading-tight tracking-tight truncate ${
                    brand.textColor || "text-slate-900"
                  }`}
                >
                  {brand.title}
                </span>
                {brand.tagline && (
                  <span className="text-[11px] font-medium text-slate-500 leading-tight truncate">
                    {brand.tagline}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Center Section - Omnisearch */}
          <div className="hidden sm:flex flex-1 min-w-0 justify-center px-3 lg:px-6">
            <div
              ref={omniRef}
              className="relative w-full max-w-xl min-w-0"
              onBlur={(e) => {
                // Focus leaving the pill+dropdown entirely (page click, tab-away) closes it.
                if (!e.currentTarget.contains(e.relatedTarget)) closeSearch();
              }}
            >
              {/* google-style rotating gradient rim: a spinning conic square clipped by the
                  overflow-hidden shell; the opaque pill masks all but the 1.5px edge. Focus ring
                  lives on the shell so it draws OUTSIDE the rim, unclipped. */}
              <div
                className={`relative overflow-hidden rounded-full p-[1.5px] transition-shadow duration-150 focus-within:ring-2 ${
                  isJumbo ? "focus-within:ring-teal-600/30" : "focus-within:ring-[#3877FC]/40"
                }`}
              >
                <span aria-hidden="true" className="absolute left-1/2 top-1/2 h-[1200px] w-[1200px] -translate-x-1/2 -translate-y-1/2">
                  <span
                    className={`absolute inset-0 motion-reduce:animate-none ${shipmentsLoading ? "animate-[spin_1s_linear_infinite]" : "animate-[spin_4s_linear_infinite]"}`}
                    style={{ background: "conic-gradient(#3877FC, #8B5CF6, #F43F5E, #F59E0B, #10B981, #3877FC)" }}
                  />
                </span>
              <div
                className="relative flex h-9 items-center gap-2 overflow-hidden rounded-full bg-slate-100 pl-3 pr-1.5 transition-colors duration-150 focus-within:bg-white"
              >
                {shipmentsLoading && (
                  /* barcode-scanner beam: sweeps across the typed AWB while we search */
                  <span
                    aria-hidden="true"
                    className="animate-omni-scan pointer-events-none absolute inset-y-0 w-20 bg-gradient-to-r from-transparent via-[#3877FC]/30 to-transparent"
                  />
                )}
                <Search className="h-4 w-4 flex-shrink-0 text-slate-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  role="combobox"
                  aria-expanded={searchOpen}
                  aria-controls="omni-listbox"
                  aria-autocomplete="list"
                  aria-activedescendant={
                    searchOpen && selectableItems[activeIndex] ? `omni-item-${activeIndex}` : undefined
                  }
                  aria-label="Search pages, AWB or order reference"
                  autoComplete="off"
                  spellCheck={false}
                  className="h-full w-full min-w-0 bg-transparent text-[13px] font-medium text-slate-700 placeholder:text-slate-400 focus:outline-none"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  onKeyDown={handleSearchKeyDown}
                />
                <kbd className="hidden md:inline-flex flex-shrink-0 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
                  ⌘K
                </kbd>
              </div>
              </div>

              {/* Anchored dropdown - replaces the old modal command palette */}
              <Transition
                show={searchOpen}
                as={React.Fragment}
                enter="transition ease-out duration-150"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <div className="absolute left-0 top-full z-50 mt-2 w-full min-w-[22rem] origin-top overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200/70">
                  <div
                    id="omni-listbox"
                    role="listbox"
                    ref={omniGlide.attachContainer}
                    className="omni-list"
                  >
                    <GlideHighlight glide={omniGlide.glide} color={glideTint} />
                    {selectableItems.length === 0 && !showShipmentsGroup ? (
                      <div className="py-10 text-center">
                        <Search className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                        <p className="text-sm font-semibold text-slate-600">No results found</p>
                        <p className="mt-1 text-xs text-slate-400">
                          Try a page name, an AWB or an order reference
                        </p>
                      </div>
                    ) : (
                      <>
                        {filteredCommands.length > 0 && (
                          <div>
                            <div className={`px-3 pb-1.5 pt-2 ${groupLabelBase}`}>Navigate</div>
                            {filteredCommands.map((item, idx) => {
                              const active = activeIndex === idx;
                              return (
                                <div
                                  key={item.path}
                                  id={`omni-item-${idx}`}
                                  role="option"
                                  aria-selected={active}
                                  ref={(node) => {
                                    if (node) omniRowRefs.current.set(idx, node);
                                    else omniRowRefs.current.delete(idx);
                                  }}
                                  className={`${rowBase} ${active ? "text-slate-900" : "text-slate-700"}`}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onMouseMove={() => {
                                    if (activeIndex !== idx) {
                                      omniNavSourceRef.current = "mouse";
                                      setActiveIndex(idx);
                                    }
                                  }}
                                  onClick={() => handleSelect({ type: "nav", item })}
                                >
                                  <span
                                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-colors duration-150 ${
                                      active ? accentTile : "bg-slate-100 text-slate-500"
                                    }`}
                                  >
                                    <item.icon className="h-4 w-4" />
                                  </span>
                                  <span className="flex-1 truncate">{item.label}</span>
                                  {item.shortcut && (
                                    <kbd className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-400">
                                      {item.shortcut}
                                    </kbd>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {showShipmentsGroup && (
                          <div>
                            <div className={`flex items-center justify-between px-3 pb-1.5 pt-3 ${groupLabelBase}`}>
                              <span>Shipments</span>
                              {shipmentsLoading && (
                                <Loader2 className="h-3 w-3 animate-spin text-slate-400" aria-hidden="true" />
                              )}
                            </div>
                            {shipmentHits.length > 0 ? (
                              shipmentHits.map((order, i) => {
                                const idx = filteredCommands.length + i;
                                const active = activeIndex === idx;
                                const statusName = order?.latest_order_status || null;
                                return (
                                  <div
                                    key={order.order_reference_number}
                                    id={`omni-item-${idx}`}
                                    role="option"
                                    aria-selected={active}
                                    ref={(node) => {
                                      if (node) omniRowRefs.current.set(idx, node);
                                      else omniRowRefs.current.delete(idx);
                                    }}
                                    className={`${rowBase} ${active ? "text-slate-900" : "text-slate-700"}`}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onMouseMove={() => {
                                      if (activeIndex !== idx) {
                                        omniNavSourceRef.current = "mouse";
                                        setActiveIndex(idx);
                                      }
                                    }}
                                    onClick={() => handleSelect({ type: "shipment", item: order })}
                                  >
                                    <span
                                      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-colors duration-150 ${
                                        active ? accentTile : "bg-slate-100 text-slate-500"
                                      }`}
                                    >
                                      <Package className="h-4 w-4" />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                      <span className="block truncate font-mono text-[13px] font-semibold text-slate-800">
                                        {order.awb_number || order.order_reference_number}
                                      </span>
                                      {order.awb_number && (
                                        <span className="block truncate text-[11px] font-medium text-slate-500">
                                          {order.order_reference_number}
                                        </span>
                                      )}
                                    </span>
                                    {statusName && (
                                      <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${accentBadge}`}>
                                        {statusName}
                                      </span>
                                    )}
                                  </div>
                                );
                              })
                            ) : shipmentsLoading ? (
                              <div className="px-3 py-2.5 text-xs font-medium text-slate-400">
                                Searching shipments...
                              </div>
                            ) : shipmentsSearched ? (
                              <div
                                key={notFoundPulse}
                                className={`mx-1 flex items-start gap-2.5 rounded-lg bg-rose-50/70 px-3 py-2.5 ring-1 ring-rose-100 ${notFoundPulse > 0 ? "animate-omni-shake" : ""}`}
                              >
                                <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-rose-100 text-rose-500">
                                  <Search className="h-3.5 w-3.5" />
                                </span>
                                <span className="min-w-0">
                                  <span className="block text-xs font-semibold text-rose-700">
                                    No shipment matches "<span className="break-all font-mono">{idQuery}</span>"
                                  </span>
                                  <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">
                                    Check the AWB or order reference and try again.
                                  </span>
                                </span>
                              </div>
                            ) : null}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/70 px-4 py-2">
                    <div className="flex items-center gap-4 text-[11px] text-slate-500">
                      <div className="flex items-center gap-1">
                        <kbd className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">↑</kbd>
                        <kbd className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">↓</kbd>
                        <span>Navigate</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <kbd className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">↵</kbd>
                        <span>Select</span>
                      </div>
                    </div>
                    <div className="text-[11px] font-medium text-slate-400">
                      <span className={`font-semibold ${accentText}`}>esc</span> to close
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
          </div>

          {/* Right Section - Actions */}
          <div className="flex items-center gap-2.5 sm:gap-3 flex-shrink-0">
            {/* Wallet - Mobile: compact balance chip, Desktop: full pill + recharge button */}
            {/* Mobile wallet balance (always visible) */}
            <button
              type="button"
              onClick={() => navigate("/wallet")}
              className={`hdr-lift md:hidden inline-flex h-9 items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 text-emerald-700 ring-1 ring-emerald-200/80 transition-[color,transform,box-shadow] duration-200 hover:-translate-y-px hover:text-emerald-800 hover:shadow-md hover:shadow-emerald-600/10 active:translate-y-0 ${focusable}`}
              title="Go to Wallet"
              aria-label={`Wallet balance ${balance == null ? "not available" : formatINR.format(balance)}`}
            >
              <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
                <span aria-hidden="true" className={`absolute inset-0 rounded-full opacity-60 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite] motion-reduce:animate-none ${variantDot[walletVariant]}`} />
                <span className={`relative h-1.5 w-1.5 rounded-full ${variantDot[walletVariant]}`} />
              </span>
              <WalletIcon className="h-4 w-4 flex-shrink-0" />
              <span className="text-xs font-semibold tabular-nums">
                {balance == null ? "-" : <RollingNumber value={balance} format={(v) => formatINR.format(v)} />}
              </span>
            </button>

            {/* Desktop wallet (full pill + recharge) */}
            <div className="hidden md:flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate("/wallet")}
                className={`hdr-lift inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-50 pl-2.5 pr-3 text-emerald-700 ring-1 ring-emerald-200/80 transition-[color,transform,box-shadow] duration-200 hover:-translate-y-px hover:text-emerald-800 hover:shadow-md hover:shadow-emerald-600/10 active:translate-y-0 ${focusable}`}
                title="Go to Wallet"
                aria-label={`Wallet balance ${balance == null ? "not available" : formatINR.format(balance)}`}
              >
                <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
                <span aria-hidden="true" className={`absolute inset-0 rounded-full opacity-60 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite] motion-reduce:animate-none ${variantDot[walletVariant]}`} />
                <span className={`relative h-1.5 w-1.5 rounded-full ${variantDot[walletVariant]}`} />
              </span>
                <WalletIcon className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm font-semibold tabular-nums">
                  {balance == null ? "-" : <RollingNumber value={balance} format={(v) => formatINR.format(v)} />}
                </span>
              </button>

              <button
                type="button"
                onClick={() => navigate("/wallet?recharge=1")}
                className={`hdr-lift inline-flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600 text-white transition-[background-color,transform,box-shadow] duration-200 hover:bg-emerald-700 hover:-translate-y-px hover:shadow-md hover:shadow-emerald-600/20 active:translate-y-0 ${focusable}`}
                title="Quick recharge"
                aria-label="Quick recharge"
              >
                <Plus className="h-4 w-4" strokeWidth={2.5} />
              </button>
            </div>

            {/* Token Timer */}
            {showTimer && (
              <div className="hidden md:flex items-center" role="status" aria-live="polite" aria-atomic="true">
                <div
                  className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 ring-1 transition-colors duration-300 ${(() => {
                    const mins = remainingMs != null ? remainingMs / 60000 : null;
                    if (mins == null) return "bg-slate-100/80 text-slate-600 ring-slate-200/70";
                    if (mins > 15) return "bg-emerald-50 text-emerald-700 ring-emerald-200/80";
                    if (mins > 5) return "bg-amber-50 text-amber-700 ring-amber-200/80";
                    if (mins > 1) return "bg-orange-50 text-orange-700 ring-orange-200/80";
                    return "bg-rose-50 text-rose-700 ring-rose-200/80";
                  })()}`}
                  title={isCritical ? "Token expiring soon - you will be logged out when the timer reaches zero" : "Session expiry timer"}
                  tabIndex={0}
                  aria-label={remainingMs != null ? `Session expires in ${formatTime(remainingMs)}` : "Session expiry not available"}
                >
                  {(() => {
                    const total = getSessionDurationMs();
                    const frac = total && remainingMs != null ? Math.max(0, Math.min(1, remainingMs / total)) : null;
                    const C = 2 * Math.PI * 6.5;
                    // Ring colour stages by time left: plenty -> emerald, then amber, orange, rose.
                    const mins = remainingMs != null ? remainingMs / 60000 : null;
                    const ringColor = mins == null ? "currentColor"
                      : mins > 15 ? "#10b981"
                      : mins > 5 ? "#f59e0b"
                      : mins > 1 ? "#f97316"
                      : "#f43f5e";
                    return frac == null ? (
                      <Clock className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                    ) : (
                      <svg className="h-4 w-4 flex-shrink-0 -rotate-90" viewBox="0 0 16 16" aria-hidden="true">
                        <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeOpacity="0.15" strokeWidth="2" />
                        <circle
                          cx="8" cy="8" r="6.5" fill="none" stroke={ringColor} strokeWidth="2" strokeLinecap="round"
                          strokeDasharray={C}
                          strokeDashoffset={C * (1 - frac)}
                          style={{ transition: "stroke-dashoffset 1s linear, stroke 300ms ease" }}
                        />
                      </svg>
                    );
                  })()}
                  <span className="text-xs font-semibold tabular-nums whitespace-nowrap">
                    {formatTime(remainingMs)}
                  </span>
                  <span className="sr-only">
                    {remainingMs != null ? `Session expires in ${formatTime(remainingMs)}` : `Session expiry not available`}
                  </span>
                </div>
              </div>
            )}

            {/* Notifications */}
            <div className="hidden sm:block relative notification-container">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className={`relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 transition-[color,transform] duration-150 hover:text-slate-900 active:scale-95 ${focusable}`}
                aria-label="Notifications"
              >
                <Bell className="w-[18px] h-[18px]" strokeWidth={2} />
                {wdAttention > 0 && (
                  <span aria-hidden="true" className="absolute top-1 right-1 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white"></span>
                )}
              </button>

              <Transition
                show={showNotifications}
                as={React.Fragment}
                enter="transition ease-out duration-150"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <div className="absolute right-0 mt-2 w-80 origin-top-right overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-slate-200/70 focus:outline-none">
                  <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                      <Bell className="h-4 w-4 text-slate-400" />
                      Notifications
                    </h3>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${accentBadge}`}>
                      New
                    </span>
                  </div>

                  {wdAttention > 0 ? (
                    <button
                      type="button"
                      onClick={() => {
                        setShowNotifications(false);
                        navigate("/weight-disputes");
                      }}
                      className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-[#3877FC]/[0.04] transition-colors"
                    >
                      <span className="mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 ring-1 ring-amber-200">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v18m-7-4l7 4 7-4M5 7l7-4 7 4M3 12h3m12 0h3" />
                        </svg>
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-slate-900">
                          {wdAttention} weight dispute{wdAttention === 1 ? "" : "s"} need{wdAttention === 1 ? "s" : ""} your response
                        </span>
                        <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">
                          Accept or dispute before the auto-accept window closes. Tap to review.
                        </span>
                      </span>
                    </button>
                  ) : (
                  <div className="px-6 py-12 text-center">
                    <div className={`mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl ${accentTile}`}>
                      <Bell className="h-5 w-5" strokeWidth={2} />
                    </div>
                    <p className="mb-1 text-sm font-semibold text-slate-900">All caught up!</p>
                    <p className="mx-auto max-w-[220px] text-xs leading-relaxed text-slate-500">
                      You're all set. We'll notify you when something important happens.
                    </p>
                  </div>
                  )}
                </div>
              </Transition>
            </div>

            {/* User Menu */}
            <Menu as="div" className="relative">
              {({ open }) => (
                <>
                  <Menu.Button
                    className={`group flex h-9 items-center gap-2 rounded-lg pl-1 pr-1.5 lg:pr-2 ${focusable}`}
                  >
                    <div
                      className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ring-2 ring-transparent transition-shadow duration-200 ${
                        isJumbo ? "group-hover:ring-[#07374a]/25" : "group-hover:ring-[#3877FC]/35"
                      }`}
                      style={{
                        background: brand.avatarGradient || accentAvatarFallback,
                      }}
                    >
                      {username?.[0]?.toUpperCase() || "U"}
                    </div>

                    <span className="hidden lg:inline text-sm font-medium text-slate-700 transition-colors duration-150 group-hover:text-slate-900 max-w-[120px] truncate">
                      {username}
                    </span>

                    <ChevronDown
                      className={`hidden lg:block h-4 w-4 flex-shrink-0 text-slate-400 transition-[color,transform] duration-150 group-hover:text-slate-600 ${
                        open ? "rotate-180" : ""
                      }`}
                      strokeWidth={2}
                    />
                  </Menu.Button>

                  <Transition
                    as={React.Fragment}
                    enter="transition ease-out duration-150"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Menu.Items className="absolute right-0 mt-2 w-64 origin-top-right overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-slate-200/70 focus:outline-none">
                      {/* One glide per menu - rows carry no hover/active backgrounds */}
                      <div ref={menuGlide.attachContainer} onMouseLeave={menuGlide.hide} className="relative">
                        <GlideHighlight glide={menuGlide.glide} color={glideTint} />

                        <div className="border-b border-slate-100 px-4 py-3">
                          <p className="truncate text-sm font-semibold text-slate-900">{username}</p>
                          <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-slate-500">
                            <Activity className="h-3 w-3" />
                            {source}
                          </p>
                        </div>

                        <div className="p-1.5">
                          <Menu.Item>
                            {({ active }) => (
                              <GlideMenuButton
                                active={active}
                                onGlide={menuGlide.move}
                                onClick={() => navigate("/change-password")}
                                className={`${
                                  active ? "text-slate-900" : "text-slate-700"
                                } relative z-[1] flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors duration-150`}
                              >
                                <Lock
                                  className={`h-4 w-4 flex-shrink-0 transition-colors duration-150 ${
                                    active ? "text-slate-600" : "text-slate-400"
                                  }`}
                                  strokeWidth={2}
                                />
                                <span>Change Password</span>
                              </GlideMenuButton>
                            )}
                          </Menu.Item>
                        </div>

                        <div className="border-t border-slate-100 p-1.5">
                          <Menu.Item>
                            {({ active }) => (
                              <GlideMenuButton
                                active={active}
                                onGlide={menuGlide.move}
                                onClick={handleLogout}
                                className={`${
                                  active ? "text-rose-700" : "text-rose-600"
                                } relative z-[1] flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors duration-150`}
                              >
                                <LogOut className="h-4 w-4 flex-shrink-0" strokeWidth={2} />
                                <span>Logout</span>
                              </GlideMenuButton>
                            )}
                          </Menu.Item>
                        </div>
                      </div>
                    </Menu.Items>
                  </Transition>
                </>
              )}
            </Menu>
          </div>

          {/* Marquees */}
          {visibleMarquees.length > 0 && (
            <div className="inside-marquee">
              <div
                style={{
                  display: "grid",
                  gridAutoRows: `${BAR_HEIGHT}px`,
                  rowGap: `${BAR_GAP}px`,
                }}
              >
                {visibleMarquees.map((m) => {
                  const vKey = m.variant || "info";
                  const v = MARQUEE_VARIANTS[vKey] || MARQUEE_VARIANTS.info;
                  const dir = m.direction === "ltr" ? "ltr" : "rtl";
                  const duration = Math.max(10, Math.min(20, Math.floor((m.text?.length || 40) / 3)));
                  return (
                    <div
                      key={m.id}
                      className="inside-marquee-item"
                      style={{
                        background: v.bg,
                        border: `1.5px solid ${v.border}`,
                        color: v.color,
                      }}
                      role="status"
                      aria-live="polite"
                    >
                      <div className="mr-2 opacity-90">{v.icon}</div>

                      <div
                        className="inside-marquee-track"
                        style={{
                          animation: `${dir === "ltr" ? "marquee-ltr" : "marquee-rtl"} ${duration}s linear infinite`,
                        }}
                      >
                        <span className="inside-marquee-text" style={{ color: v.color }}>
                          {m.text}
                        </span>
                        <span className="inside-marquee-text" style={{ color: v.color }} aria-hidden="true">
                          {m.text}
                        </span>
                        <span className="inside-marquee-text" style={{ color: v.color }} aria-hidden="true">
                          {m.text}
                        </span>
                      </div>

                      {m.cta && (
                        <button
                          type="button"
                          onClick={() => (m.cta.onClick ? m.cta.onClick() : m.cta.to ? navigate(m.cta.to) : null)}
                          className="inside-marquee-cta"
                          style={{
                            background: "rgba(255,255,255,.8)",
                            color: v.color,
                            border: `1px solid ${v.border}`,
                          }}
                        >
                          {m.cta.label}
                        </button>
                      )}

                      {(m.dismissible ?? true) && (
                        <button
                          type="button"
                          onClick={() => handleDismiss(m.id)}
                          className="inside-marquee-dismiss"
                          aria-label="Dismiss"
                          title="Dismiss"
                          style={{ color: v.color }}
                        >
                          <Close className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
}
